import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

const authorOkType = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().email().optional()
})

export const languageOkType = z.object({
  id: z.string(),
  name: z.string(),
  is_master: z.boolean().optional()
})

const documentVersionOkType = z
  .discriminatedUnion("status", [
    z.object({ status: z.literal("unclassified") }),
    z.object({ status: z.literal("published") }),
    z.object({ status: z.literal("release"), release_id: z.string() }),
    z.object({ status: z.literal("archived") })
  ])
  .and(
    z.object({
      version_id: z.string(),
      tags: z.array(z.string()),
      custom_type_label: z.string(),
      preview_summary: z.string().optional(),
      preview_image: z.string().optional(),
      last_modified_date: z.coerce.date(),
      author: authorOkType,
      uid: z.string().optional()
    })
  )

export const documentMetaOkType = z.object({
  id: z.string(),
  title: z
    .string()
    .nullish()
    .transform((title) => title ?? "Untitled"),
  custom_type_id: z.string(),
  group_lang_id: z.string(),
  language: languageOkType,
  versions: z.array(documentVersionOkType)
})

const documentsSearchAPISchemaOkType = z.object({
  total: z.number(),
  cursor: z.string().optional(),
  results: z.array(documentMetaOkType)
})

export type Language = z.TypeOf<typeof languageOkType>
export type DocumentSearchApi = z.TypeOf<typeof documentsSearchAPISchemaOkType>
export type DocumentMeta = z.TypeOf<typeof documentMetaOkType>
export type DocumentVersion = z.TypeOf<typeof documentVersionOkType>
export type DocumentStatus = DocumentVersion["status"]

export function searchDocuments(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  searchTerm: string,
  limit?: number,
  cursor?: string
): Promise<DocumentSearchApi> {
  const url = new URL("documents", baseUrl)
  if (limit) url.searchParams.set("limit", limit.toString())
  if (cursor) url.searchParams.set("cursor", cursor)
  url.search += `&${searchTerm}`

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: documentsSearchAPISchemaOkType
  })
}

export type SearchDocumentNames =
  | "tags"
  | "language"
  | "customTypes"
  | "status"
  | "term"
  | "group_lang_id"
  | "document_id"
  | "uid"
  | "authors"

export function createSearchParams<T extends string>(
  params: { name: T; values: string[] }[]
): [string, string][] {
  return params.reduce<[string, string][]>((queryParams, currentVariable) => {
    // if current variable has no values, return queryParams as is without adding the current empty variable
    if (currentVariable.values.every((value) => value === ""))
      return queryParams

    const arr = currentVariable.values.map<[string, string]>((value) => [
      currentVariable.name,
      value
    ])
    return queryParams.concat(arr)
  }, [])
}

export function getDocumentByDocumentId(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  id: string
): Promise<DocumentMeta> {
  return request(new URL(`documents/${id}`, baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: documentMetaOkType
  })
}
